import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import Icon from '../components/ui/Icon';
import Button from '../components/ui/Button';
import KpiCard from '../components/ui/KpiCard';
import { EmptyState, ErrorState } from '../components/ui/States';
import StatusPill from '../components/users/StatusPill';
import FuelPill from '../components/users/FuelPill';
import RewardLevels from '../components/users/RewardLevels';
import { toProfileView } from '../components/users/profileAdapter';
import { toRewardsView } from '../components/users/rewardAdapter';
import { PLACEHOLDER } from '../components/users/userAdapter';
import { SUSPEND_AVAILABLE, usersApi } from '../api/users';
import { canSuspendUsers } from '../auth/permissions';
import { copyText } from '../utils/clipboard';

const USER_ID_PATTERN = /^[A-Za-z0-9_-]{1,40}$/;

const card = 'bg-surface-container-lowest rounded-xl border border-outline-variant/30 shadow-xs';

function CopyButton({ value, label }) {
  return (
    <button
      type="button"
      onClick={() => copyText(value)}
      aria-label={label}
      title="Copy"
      className="text-on-surface-variant hover:text-primary transition-colors p-0.5 rounded outline-none focus-visible:ring-2 focus-visible:ring-status-ok-dot"
    >
      <Icon name="content_copy" size={14} />
    </button>
  );
}

function Detail({ label, children }) {
  return (
    <div className="min-w-0">
      <dt className="text-[11px] font-semibold uppercase tracking-wider text-on-surface-variant">{label}</dt>
      <dd className="mt-1 text-[13px] text-on-surface flex flex-wrap items-center gap-1.5 min-w-0">{children}</dd>
    </div>
  );
}

function ProfileSkeleton() {
  return (
    <div className="flex flex-col gap-6 animate-pulse" role="status" aria-label="Loading user profile">
      <div className={`${card} p-6 flex items-center gap-4`}>
        <div className="w-16 h-16 rounded-full bg-surface-container" />
        <div className="space-y-2">
          <div className="h-5 w-48 bg-surface-container rounded" />
          <div className="h-3 w-28 bg-surface-container-low rounded" />
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-5">
        {Array.from({ length: 4 }, (_, i) => (
          <div key={i} className={`${card} h-28`} />
        ))}
      </div>
      <div className={`${card} h-72`} />
    </div>
  );
}

/** User detail: identity, account details, scan/reward KPIs and the reward ladder. */
export default function UserProfilePage() {
  const { userId = '' } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  // Return to the list exactly as it was (page, search, sort) when opened from it
  const backTo = `/users${typeof location.state?.from === 'string' ? location.state.from : ''}`;
  const validId = USER_ID_PATTERN.test(userId);

  const { data, error, isPending, refetch } = useQuery({
    queryKey: ['user', userId],
    queryFn: ({ signal }) => usersApi.getProfile(userId, { signal }),
    enabled: validId,
    staleTime: 30_000,
    retry: (count, err) => (err?.status === 0 || err?.status >= 500) && count < 1,
  });

  // Separate from the profile so a failure here only affects the rewards section and summary cards
  const rewardsQuery = useQuery({
    queryKey: ['user', userId, 'rewards'],
    queryFn: ({ signal }) => usersApi.getUserRewards(userId, { signal }),
    enabled: validId && Boolean(data),
    staleTime: 30_000,
    retry: (count, err) => (err?.status === 0 || err?.status >= 500) && count < 1,
  });

  const backLink = (
    <Link
      to={backTo}
      className="inline-flex items-center gap-1 self-start text-xs font-semibold text-secondary hover:underline rounded outline-none focus-visible:ring-2 focus-visible:ring-status-ok-dot"
    >
      <Icon name="arrow_back" size={16} />
      Back to users
    </Link>
  );

  if (!validId || error?.status === 404) {
    return (
      <>
        {backLink}
        <section className={card}>
          <EmptyState
            tone="neutral"
            icon="person_off"
            title="User not found"
            description={validId ? `There's no user with ID ${userId}.` : 'This link doesn’t contain a valid user ID.'}
            action={
              <Button variant="secondary" icon="group" onClick={() => navigate(backTo)}>
                Back to users
              </Button>
            }
          />
        </section>
      </>
    );
  }

  if (error) {
    const forbidden = error.status === 403;
    return (
      <>
        {backLink}
        <section className={card}>
          <ErrorState
            icon={forbidden ? 'lock' : 'cloud_off'}
            title={forbidden ? "You don't have permission to view this user" : "Couldn't load this user"}
            description={forbidden ? undefined : error.message}
            retryAfter={error.status === 429 ? 5 : 0}
            onRetry={forbidden ? undefined : () => refetch()}
          />
        </section>
      </>
    );
  }

  if (isPending) {
    return (
      <>
        {backLink}
        <ProfileSkeleton />
      </>
    );
  }

  const user = toProfileView(data);
  const rewards = rewardsQuery.data ? toRewardsView(rewardsQuery.data) : null;
  const rewardsError = rewards ? null : rewardsQuery.error;
  const rewardsLoading = !rewards && (rewardsQuery.isPending || rewardsQuery.isFetching);
  const showSuspend = canSuspendUsers();

  return (
    <>
      {backLink}

      <section className={`${card} p-5 sm:p-6`} aria-labelledby="profile-name">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div className="flex items-center gap-4 min-w-0">
            <div
              className={`w-16 h-16 rounded-full text-xl font-bold flex items-center justify-center shrink-0 shadow-xs ${user.avatarClass}`}
              aria-hidden="true"
            >
              {user.initials}
            </div>
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                <h1 id="profile-name" className="text-2xl font-bold text-on-surface tracking-tight truncate">
                  {user.name}
                </h1>
                <StatusPill status={user.status} />
              </div>
              <div className="mt-1 flex items-center gap-1.5 font-data-mono text-xs text-on-surface-variant">
                {user.userId}
                <CopyButton value={user.userId} label="Copy user ID" />
              </div>
            </div>
          </div>

          {showSuspend && (
            <span className="self-start md:self-auto" title={SUSPEND_AVAILABLE ? undefined : "Suspending users isn't available yet"}>
              <Button variant="danger" icon="block" disabled={!SUSPEND_AVAILABLE}>
                Suspend User
              </Button>
            </span>
          )}
        </div>

        <dl className="mt-6 pt-5 border-t border-surface-container grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-5">
          <Detail label="Email">
            {user.email ? (
              <>
                <span className="break-all">{user.email}</span>
                <CopyButton value={user.email} label={`Copy email address of ${user.name}`} />
              </>
            ) : (
              PLACEHOLDER
            )}
          </Detail>
          <Detail label="Phone number">
            <span className="font-data-mono text-xs">{user.phone}</span>
          </Detail>
          <Detail label="Member since">{user.memberSince ?? PLACEHOLDER}</Detail>
          <Detail label="Sign-in method">{user.signInMethod}</Detail>
          <Detail label="Fuel preference">
            <FuelPill fuel={user.fuel} />
            {user.fuelFrequency && <span className="text-xs text-on-surface-variant">· {user.fuelFrequency}</span>}
          </Detail>
          <Detail label="Onboarding quiz">{user.onboardingQuiz}</Detail>
        </dl>
      </section>

      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-5">
        {rewardsLoading ? (
          Array.from({ length: 4 }, (_, i) => <div key={i} className={`${card} h-28 animate-pulse`} />)
        ) : (
          <>
            <KpiCard
              label="Total scans"
              value={rewards?.approved ?? PLACEHOLDER}
              icon="document_scanner"
              footer={rewards && <span>Approved receipt scans</span>}
            />
            <KpiCard
              label="Current level"
              value={rewards?.currentLevelLabel ?? PLACEHOLDER}
              icon="military_tech"
              footer={
                rewards && (
                  <span>
                    {rewards.reachedCount} of {rewards.levels.length} milestones reached
                  </span>
                )
              }
            />
            <KpiCard
              label="Rewards unlocked"
              value={rewards?.unlockedCount ?? PLACEHOLDER}
              suffix={rewards ? `of ${rewards.levels.length}` : undefined}
              icon="redeem"
              footer={rewards && <span>{rewards.unlockedCount ? 'Coupons earned by this user' : 'No coupons unlocked yet'}</span>}
            />
            <KpiCard
              label="Next reward"
              value={rewards?.next?.scansRemaining ?? PLACEHOLDER}
              suffix={rewards?.next ? 'scans to go' : undefined}
              icon="flag"
              footer={
                rewards && (
                  <span className="truncate">
                    {rewards.next ? rewards.next.label : rewards.allTiersCompleted ? 'All milestones reached' : 'No next reward'}
                  </span>
                )
              }
            />
          </>
        )}
      </div>

      <RewardLevels rewards={rewards} isLoading={rewardsLoading} error={rewardsError} onRetry={() => rewardsQuery.refetch()} />
    </>
  );
}
